import React, { useState } from 'react';
import { CloudDownload } from 'lucide-react';
import toolkitService from '../../services/ToolkitService';
import Button from '../shared/Button';
import { STEP, stepIcon } from './ToolkitFileStep';

// The Opera Cloud configuration every ledger screen loads before it can post — the desktop's "Load from Opera Cloud"
// button, shared by AR Ledger, Guest Ledger, Deposit Ledger and Deposit Folio.
//
// useToolkitConfig(ctx, { paymentMethods, guaranteeCodes, transactionCodeKind }) loads, through the project's own
// connection: the property currency, the active cashiers, and optionally the payment methods, the guarantee codes and
// a transaction-code parameter ('ar-transaction-code' = AR_BAL_TRX_CODE, 'guest-ledger-transaction-code' =
// GL_BAL_FORWARD_TRX_CODE; 'manual' = a field with no lookup). Every value stays editable — the user chooses the cashier and payment method.
//
// <ToolkitConfigStep config number> renders the step; <CurrencyConversion config> the optional v5 → property rate.

const errorOf = (err) => err?.response?.data?.message || err?.response?.data?.error?.message || err?.message;

export const useToolkitConfig = (ctx, { paymentMethods = false, guaranteeCodes = false, transactionCodeKind = null } = {}) => {
    const [load, setLoad] = useState({ state: STEP.IDLE, message: '' });
    const [currencyCode, setCurrencyCode] = useState('');
    const [cashiers, setCashiers] = useState([]);
    const [cashierId, setCashierId] = useState('');
    const [methods, setMethods] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [guarantees, setGuarantees] = useState([]);
    const [guaranteeCode, setGuaranteeCode] = useState('');
    const [transactionCode, setTransactionCode] = useState('');
    // Optional currency conversion: post amount x rate, in the property's currency.
    const [convert, setConvert] = useState(false);
    const [sourceCurrency, setSourceCurrency] = useState('');
    const [rate, setRate] = useState('');

    const loadFromCloud = async () => {
        setLoad({ state: STEP.BUSY, message: 'Loading from Opera Cloud…' });
        try {
            const calls = [
                toolkitService.lookup(ctx, 'hotel-currency'),
                toolkitService.lookup(ctx, 'cashiers'),
                paymentMethods ? toolkitService.lookup(ctx, 'payment-methods') : null,
                guaranteeCodes ? toolkitService.lookup(ctx, 'guarantee-codes') : null,
                transactionCodeKind && transactionCodeKind !== 'manual' ? toolkitService.lookup(ctx, transactionCodeKind) : null,
            ];
            const [cur, cash, pm, gc, trx] = await Promise.all(calls.map((c) => c || Promise.resolve(null)));
            if (cur?.data?.data) setCurrencyCode(cur.data.data);
            setCashiers(cash?.data?.data || []);
            if (pm) setMethods((Array.isArray(pm.data.data) ? pm.data.data : []).filter((m) => !m.inactive));
            if (gc) setGuarantees(gc.data.data || []);
            const code = trx ? (trx.data.data?.value || trx.data.data?.parameterValue || '') : '';
            if (trx) setTransactionCode(code);
            const parts = [`currency ${cur?.data?.data || '—'}`, `${(cash?.data?.data || []).length} active cashier(s)`];
            if (pm) parts.push(`${(pm.data.data || []).length} payment method(s)`);
            if (gc) parts.push(`${(gc.data.data || []).length} guarantee code(s)`);
            if (trx) parts.push(code ? `transaction code ${code}` : 'NO transaction code');
            setLoad(trx && !code
                ? { state: STEP.ERROR, message: `Loaded ${parts.join(', ')} — the transaction code parameter is not configured in Opera Cloud.` }
                : { state: STEP.DONE, message: `Loaded ${parts.join(', ')}.` });
        } catch (err) {
            setLoad({ state: STEP.ERROR, message: errorOf(err) || 'Failed to load the Opera Cloud configuration' });
        }
    };

    const rateValid = !convert || (sourceCurrency.trim() && parseFloat(rate) > 0);
    // amount × rate when converting, as the desktop sends it (6 dp; OHIP rounds to the currency).
    const convertAmount = (amount) => (convert ? (parseFloat(amount) * parseFloat(rate)).toFixed(6) : undefined);

    return {
        load, loadFromCloud, ctx,
        currencyCode, setCurrencyCode, cashiers, cashierId, setCashierId,
        methods, paymentMethod, setPaymentMethod, guarantees, guaranteeCode, setGuaranteeCode,
        transactionCode, setTransactionCode, transactionCodeKind,
        convert, setConvert, sourceCurrency, setSourceCurrency, rate, setRate, rateValid, convertAmount,
    };
};

const describe = (m, codeKey, ...labels) => {
    const code = m[codeKey];
    const label = labels.map((k) => m[k]).find(Boolean);
    return label && label !== code ? `${code} — ${label}` : code;
};

export function ToolkitConfigStep({ config, number, ready, children }) {
    const c = config;
    return (
        <div className="tkb-step">
            <div className="tkb-step-head"><span className="tkb-num">{number}</span> Opera Cloud configuration {stepIcon(c.load.state)}</div>
            <Button variant="secondary" onClick={c.loadFromCloud} disabled={!ready || c.load.state === STEP.BUSY}>
                <CloudDownload className="icon" /> Load from Opera Cloud
            </Button>
            {c.load.message && <p className={`tkb-msg${c.load.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{c.load.message}</p>}
            <div className="tkb-row" style={{ marginTop: '0.75rem' }}>
                <label>
                    <span>Cashier</span>
                    <select value={c.cashierId} onChange={(e) => c.setCashierId(e.target.value)} disabled={!c.cashiers.length}>
                        <option value="">{c.cashiers.length ? 'Choose a cashier…' : 'Load the configuration first'}</option>
                        {c.cashiers.map((x) => <option key={x.code} value={x.code}>{describe(x, 'code', 'name', 'description')}</option>)}
                    </select>
                </label>
                <label><span>Currency</span><input type="text" value={c.currencyCode} onChange={(e) => c.setCurrencyCode(e.target.value.toUpperCase())} style={{ width: 90 }} /></label>
                {c.methods.length > 0 && (
                    <label>
                        <span>Payment method</span>
                        <select value={c.paymentMethod} onChange={(e) => c.setPaymentMethod(e.target.value)}>
                            <option value="">Choose…</option>
                            {c.methods.map((m) => <option key={m.paymentMethod} value={m.paymentMethod}>{describe(m, 'paymentMethod', 'description')}</option>)}
                        </select>
                    </label>
                )}
                {c.guarantees.length > 0 && (
                    <label>
                        <span>Guarantee code</span>
                        <select value={c.guaranteeCode} onChange={(e) => c.setGuaranteeCode(e.target.value)}>
                            <option value="">Choose…</option>
                            {c.guarantees.map((g) => <option key={g.code} value={g.code}>{describe(g, 'code', 'shortDescription')}</option>)}
                        </select>
                    </label>
                )}
                {c.transactionCodeKind !== null && (
                    <label><span>Transaction code</span><input type="text" value={c.transactionCode} onChange={(e) => c.setTransactionCode(e.target.value)} style={{ width: 140 }} /></label>
                )}
                {children}
            </div>
            <CurrencyConversion config={c} />
        </div>
    );
}

export function CurrencyConversion({ config: c }) {
    const preview = parseFloat(c.rate) > 0
        ? `1,000 ${c.sourceCurrency || '???'} = ${(1000 * parseFloat(c.rate)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c.currencyCode || '???'}`
        : '';
    return (
        <>
            <div className="tkb-row" style={{ marginTop: '0.6rem' }}>
                <label className="tkb-check">
                    <input type="checkbox" checked={c.convert} onChange={(e) => c.setConvert(e.target.checked)} />
                    <span>Convert currency</span>
                </label>
                {c.convert && (
                    <>
                        <label><span>v5 currency</span><input type="text" value={c.sourceCurrency} onChange={(e) => c.setSourceCurrency(e.target.value.toUpperCase())} style={{ width: 90 }} /></label>
                        <label><span>Rate (v5 → {c.currencyCode || 'property'})</span><input type="text" value={c.rate} onChange={(e) => c.setRate(e.target.value)} style={{ width: 120 }} /></label>
                    </>
                )}
            </div>
            {c.convert && preview && <p className="tkb-hint">{preview}.</p>}
        </>
    );
}

// What a screen still needs before it may post — shown under the grid so a disabled button explains itself.
export const missingConfig = (c, extra = []) => [
    !c.cashierId && 'a cashier',
    !c.currencyCode && 'a currency',
    c.transactionCodeKind !== null && !c.transactionCode && 'the transaction code',
    !c.rateValid && 'a v5 currency and rate',
    ...extra,
].filter(Boolean);
